import React, { useRef, useContext, useEffect, useState } from "react";
import { clone, get, isEmpty } from "lodash";
import styled from "styled-components";

import AppContext from "../context/AppContext";

interface GridProps {
    centerobjects?: Number;
    shouldbeimportant?: Number;
}

const Container = styled.div`
    height: 100vh;
    margin: 0 auto;
    overflow: hidden;
    @media (max-width: 1024px) and (orientation: portrait) {
        overflow: auto;
        max-width: 100%;
    }
    @media (min-width: 1025px) and (orientation: portrait) {
        display: flex;
        flex-direction: column;
        place-content: center;
    }

    @media (min-width: 600px) and (orientation: landscape) {
        display: flex;
        flex-direction: column;
        place-content: center;
    }
`;

const Row = styled.div<GridProps>`
    width: 100%;
    display: flex;
    height: 50%;
    padding: 2px 0;
    box-sizing: border-box;
    flex-wrap: nowrap !important; // We want to crop images on width rather than wrap
    &:only-child {
        height: 100%;
        padding: 0;
    }
    @media (max-width: 1024px) and (orientation: portrait) {
        height: auto !important; // Important because it gets overwritten by the Material-UI styles
        flex-wrap: wrap !important; // Wrap on mobile to get only one column
        padding: 0;
    }

    align-items: ${props => (props.centerobjects === 1 ? "center;" : "none")};
    justify-content: center;
`;

const Item = styled.div<GridProps>`
    cursor: pointer;
    position: relative;
    overflow: hidden;
    max-height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 2px;
    flex: ${props =>
        props.shouldbeimportant === 1
            ? "0 1 auto !important"
            : "0 1 auto"}; // So that cells are sized by their content size // Important when 3 items.
    @media (max-width: 1024px) and (orientation: portrait) {
        max-height: 80vh;
        min-width: unset !important;
        flex: 1 1 auto;
        margin: 0 !important; // To remove white lines at edges on mobile
    }
    box-sizing: border-box;
`;

const ItemImage = styled.img`
    height: 100%;
    object-fit: cover;
    @media (max-width: 1024px) and (orientation: portrait) {
        height: auto;
        width: 100vw !important;
    }
`;

const TitleBar = styled.div`
    background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.6) 0%,
        rgba(0, 0, 0, 0.35) 30%,
        rgba(0, 0, 0, 0.1) 75%,
        rgba(0, 0, 0, 0.05) 85%,
        rgba(0, 0, 0, 0) 100%
    );
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 65px;
    z-index: 1;
    display: flex;
    justify-content: flex-end;
    align-items: center;
`;

const TitleBarMainMenu = styled.div`
    background: ${({ theme }) => get(theme, "modelSelector.item.titleBarMenu.backgroundColor", "var(--primary-color)")};
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 65px;
    z-index: 1;
    display: flex;
    justify-content: flex-end;
    align-items: center;
`;

const TitleBarText = styled.span`
    color: white;
    font-size: 22px;
    padding: 0 5%;
    @media (max-width: 1024px) and (orientation: landscape) {
        font-size: 16px;
    }
`;

export const ImagesContainer = ({ items, onItemClick }: { items: any; onItemClick: Function }) => {
    const appContext = useContext(AppContext);

    const imageRefs = useRef<Array<HTMLImageElement | null>>([]);
    const containerRefs = useRef<Array<HTMLDivElement | null>>([]);

    const [loadedImages, setLoadedImages] = useState<boolean[]>([]);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    let imageHeightAfterRescale = 0;

    const isAllLoaded = items && items.every((_: any, i: number) => loadedImages[i]);
    useEffect(() => {
        if (isEmpty(items)) {
            appContext.setIsLoading(true);
        } else if (isAllLoaded) {
            appContext.setIsLoading(false);
        }
    }, [items, isAllLoaded, appContext]);

    useEffect(() => {
        const handleResize = () => {
            if (windowHeight !== window.innerHeight) setWindowHeight(window.innerHeight);
            if (windowWidth !== window.innerWidth) setWindowWidth(window.innerWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [windowHeight, windowWidth]);

    if (!items) return null;

    const rows: any[][] = [];
    if (items.length < 2) {
        rows.push(items);
    } else if (items.length < 4) {
        let imageWidth = 0;
        let imageScale = 0;
        let imageHeight = 0;

        if (isAllLoaded) {
            imageRefs.current.forEach((image, i) => {
                if (!image) return;
                imageWidth += image.naturalWidth;
                imageHeight = image.naturalHeight > imageHeight ? image.naturalHeight : imageHeight;
            });
        }

        imageScale = windowWidth / imageWidth;
        imageHeightAfterRescale = Math.round(imageHeight * imageScale);

        if (imageHeightAfterRescale < windowHeight * 0.5) {
            const cutOff = Math.ceil(items.length / 2);
            rows.push(items.slice(0, cutOff));
            rows.push(items.slice(cutOff));
        } else {
            rows.push(items);
        }
    } else {
        const cutOff = Math.floor(items.length / 2);
        rows.push(items.slice(0, cutOff));
        rows.push(items.slice(cutOff));
    }

    const targetWidths: number[] = [];

    let maxWidth: string | undefined;
    if (isAllLoaded) {
        // Calculate the wanted width for each row and set max-width
        // of the entire container to the shortest row.
        let firstRowWidth = 0;
        let secondRowWidth = 0;

        imageRefs.current.forEach((image, i) => {
            const container = containerRefs.current[i];
            if (!image || !container) return;

            const aspect = image.naturalWidth / image.naturalHeight;
            const targetWidth = Math.round(aspect * container.clientHeight);
            targetWidths[i] = targetWidth;
            if (i < rows[0].length) firstRowWidth += targetWidth;
            else secondRowWidth += targetWidth;
        });

        let newMaxWidth = rows.length === 1 ? firstRowWidth : Math.min(firstRowWidth, secondRowWidth);

        const shouldCoverAll = (items.length === 3 || items.length === 2) && rows.length === 2;
        newMaxWidth = shouldCoverAll ? Math.max(firstRowWidth, secondRowWidth) : newMaxWidth;

        maxWidth = `${Math.round(newMaxWidth)}px`;
    }

    const loadedImagesClosure = clone(loadedImages);
    const handleImageLoad = (index: number) => {
        // Directly modify the closure variable so two image load events without
        // a re-render between doesn't overwrite each others changes.
        loadedImagesClosure[index] = true;
        setLoadedImages(clone(loadedImagesClosure));
    };

    const renderImage = (itemIndex: number, targetWidth: number, imageUrl: string, name: string) => {
        // Explicitly set width when scaling up to maintain aspect ratio.
        let width: string | undefined;
        const image = imageRefs.current[itemIndex];
        if (loadedImages[itemIndex] && image && image.naturalWidth < targetWidth) {
            width = `${targetWidth}px`;
        }

        return (
            <ItemImage
                ref={el => {
                    imageRefs.current[itemIndex] = el;
                }}
                src={imageUrl}
                onLoad={() => handleImageLoad(itemIndex)}
                style={{ width }}
                alt={name}
            />
        );
    };

    const renderImageTitle = (type: string, name: string) => {
        return type === "MAIN" ? (
            <TitleBarMainMenu>
                <TitleBarText>{name}</TitleBarText>
            </TitleBarMainMenu>
        ) : (
            <TitleBar>
                <TitleBarText>{name}</TitleBarText>
            </TitleBar>
        );
    };

    const renderImageContainer = (itemIndex: number, numItems: number, item: any) => {
        // Set min-width so the most narrow image doesn't become too narrow.
        let minWidth: string | undefined;
        let height: string | undefined;
        const targetWidth = targetWidths[itemIndex];

        const shouldSkipCalcMinwidth = numItems === 3 || numItems === 2;

        if (targetWidth && !shouldSkipCalcMinwidth) {
            minWidth = `min(calc(${100 / numItems}% - 4px), ${targetWidth}px)`;
        }

        if ((items.length === 3 || items.length === 2) && rows.length === 1) {
            height = `${imageHeightAfterRescale}px`;
        } else {
            height = `100%`;
        }

        const shouldBeImportant = items.length === 3 || items.length === 2 ? 1 : 0;

        return (
            <Item
                key={item.imageUrl}
                ref={el => {
                    containerRefs.current[itemIndex] = el;
                }}
                onClick={() => {
                    onItemClick(item.id, item.name, item.config);
                }}
                style={{
                    opacity: isAllLoaded ? 1 : 0,
                    minWidth,
                    height,
                }}
                shouldbeimportant={shouldBeImportant}
            >
                {renderImage(itemIndex, targetWidth, item.imageUrl, item.name)}
                {renderImageTitle(item.type, item.name)}
            </Item>
        );
    };

    let longestImageRow = 0;
    let rowHeight: string | undefined;

    const renderRow = (rowIndex: number, row: any) => {
        const centerObject = rows.length === 1 && (items.length === 3 || items.length === 2) ? 1 : 0;

        if (items.length === 3 && rows.length === 2) {
            let localWidth = 0;
            let imageHeight = 0;
            let localTw = 0;
            row.forEach((item: any, index: number) => {
                let itemIndex = index;

                for (let i = 0; i < rowIndex; i += 1) {
                    itemIndex += rows[i].length;
                }
                localTw += targetWidths[itemIndex];
                const image = imageRefs.current[itemIndex];
                if (!image) return;
                localWidth += image.naturalWidth;
                imageHeight = image.naturalHeight > imageHeight ? image.naturalHeight : imageHeight;
                localTw += targetWidths[itemIndex];
            });
            localTw = Math.max(localTw, windowWidth / row.length);
            longestImageRow = localWidth > longestImageRow ? localWidth : longestImageRow;
            const imageHeightAfterScale = Math.round(imageHeight * (windowWidth / longestImageRow));

            if (localTw > windowWidth && row.length > 1 && imageHeightAfterScale < windowHeight / 2) {
                rowHeight = `${imageHeightAfterScale}px`;
            }
        }

        return (
            <Row
                key={rowIndex}
                centerobjects={centerObject}
                style={{
                    height: rowHeight,
                }}
            >
                {row.map((item: any, index: number) => {
                    let itemIndex = index;
                    for (let i = 0; i < rowIndex; i += 1) {
                        itemIndex += rows[i].length;
                    }
                    const numItems = rows[rowIndex].length;
                    return renderImageContainer(itemIndex, numItems, item);
                })}
            </Row>
        );
    };

    return (
        <Container style={{ maxWidth }}>
            {rows.map((row: any, rowIndex: number) => {
                return renderRow(rowIndex, row);
            })}
        </Container>
    );
};
